import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { ButtonComponent } from '../../components/button/button.component';
import { InputComponent } from '../../components/input/input.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import TYPES from '../../constants/types';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { DATABASE_SERVICE, DatabaseService } from '../../services/database.service';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HeaderComponent } from '../../components/header/header.component';
import { debounceTime } from 'rxjs';
import { Invite } from '../../models/invite';
import { PsychologistService } from '../../services/psychologist.service';
import { Psychologist } from '../../models/psychologists';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-invite-patient',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    TranslatePipe,
    MatDatepickerModule,
    MatInputModule,
    MatSelectModule,
    HeaderComponent
],
  templateUrl: './invite-patient.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './invite-patient.component.scss'
})
export class InvitePatientComponent {
  
  inviteForm: FormGroup;
  emailControl: UntypedFormControl;
  firstNameControl: UntypedFormControl;
  lastNameControl: UntypedFormControl;
  dialCodeControl: UntypedFormControl;
  phoneNumberControl: UntypedFormControl;
  birthdateControl: UntypedFormControl;
  isSaveButtonDisabled:boolean;
  
  authService: AuthService = inject(AUTH_SERVICE);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService<Psychologist>);
  databaseService: DatabaseService<Invite> = inject(DATABASE_SERVICE);
  translate: TranslateService = inject(TranslateService);
  snackBar: MatSnackBar = inject(MatSnackBar);

  BUTTON_TYPES: any;
  TABLES: any;
  COUNTRY_GROUPS: any;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router 
  ) {
    this.emailControl = new UntypedFormControl('', [Validators.required, Validators.email]);
    this.emailControl.valueChanges.pipe(debounceTime(1000)).subscribe(
      (value) => {
        if(this.emailControl.valid){
          console.log(value);
        }
      }
    );

    this.firstNameControl = new UntypedFormControl('', [Validators.required]);
    this.lastNameControl = new UntypedFormControl('', [Validators.required]);
    this.birthdateControl = new UntypedFormControl('', []);
    this.dialCodeControl = new UntypedFormControl('', []);
    this.phoneNumberControl = new UntypedFormControl('', []);
    
    this.inviteForm = this.formBuilder.group({
      email: this.emailControl,
      firstName: this.firstNameControl,
      lastName: this.lastNameControl,
      dialCode: this.dialCodeControl,
      phoneNumber: this.phoneNumberControl,
      birthdate: this.birthdateControl,
    });

    this.isSaveButtonDisabled = this.inviteForm.invalid;
    this.inviteForm.statusChanges.subscribe(
      (event => {
        this.isSaveButtonDisabled = this.inviteForm.invalid;
      })
    );

    this.BUTTON_TYPES = TYPES.UI.BUTTON;
    this.TABLES = TYPES.DATABASE.TABLES;
    this.COUNTRY_GROUPS = TYPES.DATABASE.COUNTRY_GROUPS;
  }

  back(){
    history.back();
  }

  async saveInvite(){
    if(!this.isFormInvalid()){
      const psychologistId = this.psychologistService.psychologist()?.id;
      if(psychologistId){
        const invite: Partial<Invite> = {};
        invite.email = this.inviteForm.value.email;
        invite.first_name = this.inviteForm.value.firstName;
        invite.last_name = this.inviteForm.value.lastName;
        invite.phone_number = (this.inviteForm.value.dialCode + this.inviteForm.value.phoneNumber).replace(/\D/g, '');
        invite.birthdate = this.toPostgresDateString(this.inviteForm.value.birthdate);
        invite.psychologist_id = psychologistId;
        invite.psychologist_name = this.psychologistService.psychologist()?.first_name + ' ' + this.psychologistService.psychologist()?.last_name
        invite.status = TYPES.DATABASE.STATUS.PENDING.code;
        invite.expires_at = new Date(new Date().setDate(new Date().getDate() + 7)).toISOString();
        invite.token = crypto.randomUUID();
          
        try{
          await this.databaseService.create(this.TABLES.INVITES, invite);
          this.router.navigate(['/home'], { replaceUrl: true });

          if('clipboard' in navigator){
            navigator.clipboard.writeText(`${environment.HOST}/invite/${invite.token}`);
          }

          const  message = this.translate.instant('info.invite_created');
          const action:string = this.translate.instant('buttons.dismiss');
          this.snackBar.open(message, action);        
        }catch(error:any){    
          const  message = this.translate.instant('info.unkonwn_error', { error: error.toString() } );       
          const action:string = this.translate.instant('buttons.dismiss');
          this.snackBar.open(message, action);
        }
      }
    }else{
      this.inviteForm.markAllAsDirty();
      this.inviteForm.markAllAsTouched();
      this.inviteForm.updateValueAndValidity();
    }
  }  

  isFormInvalid(): boolean{
    return this.inviteForm.invalid;
  }

  private toPostgresDateString(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD
  }

  sortCountriesByTranslatedName(countries: any[]){
    return countries.sort(
      (a, b) => {
        return this.translate.instant(a.name).localeCompare(this.translate.instant(b.name))
      }
    )
  }

}
